// @ts-strict-ignore
import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, NoStyleButton } from "@aviary/components";
import { l } from "@aviary/locales/i18n";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { ProductImage, type ProductImageProps } from "../ProductImage";

import { ProductImageGalleryModal } from "./ProductImageGalleryModal";

import * as styles from "./ProductImageGallery.styles";

interface Props extends Omit<ProductImageProps, "size" | "image" | "count"> {
  /**
   * The main large image to display
   */
  mainImage: string;
  /**
   * An array of images to display in the gallery and modal
   */
  images?: string[];
  /**
   * Determines if the mainImage should be borderless
   */
  isBorderless?: boolean;
  /**
   * Enables the zoom modal functionality
   */
  showZoom?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/molecules/ProductImageGallery
 */
const ProductImageGallery = ({
  availabilityStatus,
  qualityPartner,
  name,
  mainImage,
  images = null,
  isBorderless = false,
  showZoom = false,
  ...rest
}: Props) => {
  const { tablet, phoneSmall } = useBreakpoints();
  const { t } = useTranslation();

  const [activeMainImage, setActiveMainImage] = useState(mainImage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageLoaded, setimageLoaded] = useState(false);

  const handleImageLoad = () => setimageLoaded(true);

  const showGallery = images?.length > 1;

  const renderMainImage = () => {
    const productImage = (
      <ProductImage
        name={name}
        image={activeMainImage}
        availabilityStatus={availabilityStatus}
        size={tablet.greaterThan ? "xxlarge" : "xlarge"}
        qualityPartner={qualityPartner}
        onLoad={handleImageLoad}
        isLoaded={imageLoaded}
        disableLazyLoading
        {...rest}
      />
    );

    if (!showZoom) {
      return (
        <div css={[styles.mainImage.base, isBorderless && styles.mainImage.borderless]}>
          {productImage}
        </div>
      );
    }

    return (
      <NoStyleButton
        onClick={() => {
          setIsModalOpen(true);
          setActiveImageIndex(images.indexOf(activeMainImage));
        }}
        css={[
          styles.mainImage.base,
          styles.imgButton.base,
          (isBorderless || !imageLoaded) && styles.mainImage.borderless,
        ]}
        aria-label={t(l.aviary.productImage.enlarge)}
      >
        {productImage}
        {!!imageLoaded && <FontAwesomeIcon icon={faSearch} css={styles.searchIcon} aria-hidden />}
      </NoStyleButton>
    );
  };

  return (
    <>
      <div css={showGallery ? styles.galleryWrapper.showGallery : styles.galleryWrapper.noGallery}>
        {!!showGallery && (
          <Arrange
            spacing={tablet.greaterThan ? "xxsmall" : "xsmall"}
            isVertical={tablet.greaterThan}
          >
            {images.map(image => (
              <NoStyleButton
                key={image}
                onClick={() => setActiveMainImage(image)}
                css={[styles.imgButton.base, image === activeMainImage && styles.imgButton.active]}
              >
                <ProductImage
                  image={image}
                  name={name}
                  size={phoneSmall.lessThan ? "xsmall" : "small"}
                />
              </NoStyleButton>
            ))}
          </Arrange>
        )}

        {renderMainImage()}
      </div>

      {!!showZoom && (
        <ProductImageGalleryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          images={images?.length > 1 ? images : [mainImage]}
          name={name}
          activeImageIndex={activeImageIndex}
          setActiveImageIndex={setActiveImageIndex}
        />
      )}
    </>
  );
};

export { ProductImageGallery };
